import React from "react";
import {
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { EventAddOn } from "types/calendar";
import { toVatCents } from "types/shared/helper/eventVatComponents";
import { RestaurantVatCountry } from "CustomHooks/useRestaurantVatCountry";
import VatSplitEditor from "./VatSplitEditor";
import "./addon-settings.scss";

type Props = {
  addOn: EventAddOn;
  onChange: (addOn: EventAddOn) => void;
  vatCountry: RestaurantVatCountry;
  restaurantId: string;
  onVatPendingChange?: (message: string | null) => void;
};

const AddOnBookingSettings = ({
  addOn,
  onChange,
  vatCountry,
  restaurantId,
  onVatPendingChange,
}: Props) => {
  const { t, i18n } = useTranslation("calendar");
  const quantityMode = addOn.wholeGroupOnly
    ? "wholeGroup"
    : addOn.minAmount != null
    ? "minimum"
    : "optional";
  const invalidMinGuests =
    addOn.minGuests != null &&
    (!Number.isInteger(addOn.minGuests) || addOn.minGuests < 1);
  const ambiguousMinimum =
    addOn.type === "perGroup" &&
    addOn.minAmount === "GROUP_SIZE" &&
    !addOn.wholeGroupOnly;
  const exampleGuests = Math.max(
    6,
    Number.isInteger(addOn.minGuests) ? addOn.minGuests ?? 0 : 0
  );
  const packageCount = addOn.type === "perGroup" ? 1 : exampleGuests;
  const minimumCount =
    addOn.minAmount === "GROUP_SIZE" ? exampleGuests : addOn.minAmount ?? 0;
  const exampleItems =
    quantityMode === "wholeGroup"
      ? packageCount
      : quantityMode === "minimum"
      ? minimumCount
      : 1;
  const formatPrice = (amount: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: "EUR",
    }).format(amount);

  const setQuantityMode = (mode: string) => {
    onChange({
      ...addOn,
      wholeGroupOnly: mode === "wholeGroup" || undefined,
      minAmount: mode === "minimum" ? addOn.minAmount ?? 1 : null,
    });
  };

  const choice = (
    value: string,
    label: string,
    description: string,
    disabled = false
  ) => (
    <FormControlLabel
      key={value}
      value={value}
      className="addon-settings__choice"
      disabled={disabled}
      control={<Radio color="primary" />}
      label={
        <>
          <strong>{t(label)}</strong>
          <span>{t(description)}</span>
        </>
      }
    />
  );

  return (
    <div className="addon-settings">
      <section
        className="addon-settings__section"
        aria-labelledby="addon-price-heading"
      >
        <h3 id="addon-price-heading">{t("Add-on price")}</h3>
        <div className="addon-settings__fields">
          <TextField
            id="addon-price"
            variant="outlined"
            size="small"
            fullWidth
            type="number"
            label={t("Gross unit price")}
            value={addOn.amount ?? ""}
            inputProps={{ min: 0, step: "0.01" }}
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
            }}
            onChange={(event) =>
              onChange({ ...addOn, amount: Number(event.target.value) })
            }
            helperText={t("Price includes VAT. Charged per selected item.")}
          />
        </div>
        <VatSplitEditor
          idPrefix="addon"
          grossCents={toVatCents(addOn.amount ?? 0)}
          vatRate={addOn.vatRate}
          vatComponents={addOn.vatComponents}
          country={vatCountry.country}
          countryLoaded={vatCountry.loaded}
          storedCountry={vatCountry.stored}
          restaurantId={restaurantId}
          onChange={(vat) => onChange({ ...addOn, ...vat })}
          onPendingChange={onVatPendingChange}
        />
        <fieldset>
          <legend id="addon-price-unit-heading">
            {t("How is the add-on counted?")}
          </legend>
          <RadioGroup
            aria-labelledby="addon-price-unit-heading"
            name="addon-price-unit"
            value={addOn.type}
            onChange={(event) =>
              onChange({
                ...addOn,
                type: event.target.value as EventAddOn["type"],
              })
            }
          >
            {choice(
              "perPerson",
              "Per guest",
              "For example, a drinks package for each participating guest."
            )}
            {choice(
              "perGroup",
              "Per item",
              "For example, bottles or cakes, independently of the number of guests.",
              addOn.minAmount === "GROUP_SIZE" &&
                !addOn.wholeGroupOnly &&
                addOn.type !== "perGroup"
            )}
          </RadioGroup>
          {addOn.minAmount === "GROUP_SIZE" && !addOn.wholeGroupOnly && (
            <p className="addon-settings__hint">
              {t(
                "Use a per-guest price for a per-guest minimum, or choose a fixed minimum for a unit price."
              )}
            </p>
          )}
        </fieldset>
      </section>

      <section
        className="addon-settings__section"
        aria-labelledby="addon-availability-heading"
      >
        <h3 id="addon-availability-heading">{t("Add-on availability")}</h3>
        <p>
          {t(
            "These limits determine whether the add-on is offered, not how many items must be booked."
          )}
        </p>
        <div className="addon-settings__fields">
          <TextField
            id="addon-min-guests"
            variant="outlined"
            size="small"
            fullWidth
            type="number"
            label={t("Minimum group size")}
            value={addOn.minGuests ?? ""}
            inputProps={{ min: 1, step: 1 }}
            error={invalidMinGuests}
            helperText={t(
              invalidMinGuests
                ? "Minimum guests must be a positive integer"
                : "Empty: available for any group size. Example: 10 means at least 10 guests."
            )}
            onChange={(event) => {
              const updated = { ...addOn };
              if (event.target.value === "") delete updated.minGuests;
              else updated.minGuests = Number(event.target.value);
              onChange(updated);
            }}
          />
          <TextField
            id="addon-stock"
            variant="outlined"
            size="small"
            fullWidth
            type="number"
            label={t("Total available items")}
            value={addOn.limited || ""}
            inputProps={{ min: 0, step: 1 }}
            onChange={(event) =>
              onChange({ ...addOn, limited: Number(event.target.value) })
            }
            helperText={t(
              "Shared across all bookings for this event. Empty or 0: unlimited."
            )}
          />
        </div>
      </section>

      <section
        className="addon-settings__section"
        aria-labelledby="addon-quantity-heading"
      >
        <h3 id="addon-quantity-heading">{t("Add-on booking quantity")}</h3>
        <RadioGroup
          aria-labelledby="addon-quantity-heading"
          name="addon-quantity-mode"
          value={quantityMode}
          onChange={(event) => setQuantityMode(event.target.value)}
        >
          {choice(
            "optional",
            "Optional, freely selectable",
            "Guests can skip this add-on or choose a quantity."
          )}
          {choice(
            "wholeGroup",
            "Optional group package",
            addOn.type === "perGroup"
              ? "Guests choose one package per booking or none. The price is charged once."
              : "Guests choose the package for everyone or not at all. The price is per guest."
          )}
          {choice(
            "minimum",
            "Required minimum quantity",
            "A minimum quantity is included automatically once the group is eligible."
          )}
        </RadioGroup>
        {quantityMode === "minimum" && (
          <fieldset className="addon-settings__dependent">
            <legend id="addon-minimum-heading">
              {t("How many items are required?")}
            </legend>
            <RadioGroup
              aria-labelledby="addon-minimum-heading"
              name="addon-minimum-mode"
              value={addOn.minAmount === "GROUP_SIZE" ? "groupSize" : "fixed"}
              onChange={(event) =>
                onChange({
                  ...addOn,
                  minAmount:
                    event.target.value === "groupSize" ? "GROUP_SIZE" : 1,
                })
              }
            >
              {choice(
                "groupSize",
                "One item per guest",
                "The minimum quantity follows the number of guests in the booking.",
                addOn.type === "perGroup"
              )}
              {choice(
                "fixed",
                "Fixed number per booking",
                "The same minimum quantity for every eligible booking."
              )}
            </RadioGroup>
            {addOn.type === "perGroup" && (
              <p>
                {t(
                  "Use a per-guest price for a per-guest minimum, or choose a fixed minimum for a unit price."
                )}
              </p>
            )}
            {addOn.minAmount !== "GROUP_SIZE" && (
              <TextField
                id="addon-minimum-quantity"
                variant="outlined"
                size="small"
                type="number"
                label={t("Required items")}
                value={addOn.minAmount ?? 0}
                inputProps={{ min: 0, step: 1 }}
                onChange={(event) =>
                  onChange({ ...addOn, minAmount: Number(event.target.value) })
                }
                helperText={t("0 means no required items.")}
              />
            )}
          </fieldset>
        )}
        {ambiguousMinimum ? (
          <p className="addon-settings__warning" role="alert">
            {t(
              "This legacy combination has different quantities across booking flows. Choose a per-guest price or a fixed minimum quantity to make billing unambiguous."
            )}
          </p>
        ) : (
          <div
            className="addon-settings__example"
            role="status"
            aria-live="polite"
          >
            <strong>{t("Example booking", { guests: exampleGuests })}</strong>
            <p>
              {quantityMode === "wholeGroup"
                ? t("Example optional group package", { items: packageCount })
                : quantityMode === "minimum"
                ? t("Example required items", { items: minimumCount })
                : t("Example optional items")}
            </p>
            {!!addOn.minGuests && !invalidMinGuests && (
              <p>
                {t("Example below minimum group size", {
                  guests: addOn.minGuests,
                })}
              </p>
            )}
            {typeof addOn.amount === "number" && Number.isFinite(addOn.amount) && (
              <p>
                {t("Example selected price", {
                  items: exampleItems,
                  price: formatPrice(addOn.amount),
                  total: formatPrice(exampleItems * addOn.amount),
                })}
              </p>
            )}
            {!!addOn.limited && <p>{t("Subject to remaining stock.")}</p>}
          </div>
        )}
      </section>
    </div>
  );
};

export default AddOnBookingSettings;
